import { useEffect, useState, useCallback } from "react";
import { Loader2, Search, Download, Filter, AlertTriangle, RefreshCw } from "lucide-react";
import api from "../services/api";
import { Button } from "../components/ui/button";

/**
 * Sprint 75 — Audit log viewer.
 *
 * Read-only admin page. Filters by actor / action / entity_type / date range
 * and a free-text search across `description`. Pagination 50/page.
 * CSV download streams the filtered result set (≤10k rows server-side cap).
 * PII rows are highlighted yellow per spec.
 */
const PAGE_SIZE = 50;

export default function AdminAudit() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    actor: "", action: "", entity_type: "",
    search: "", pii_only: false, start: "", end: "",
  });
  const [skip, setSkip] = useState(0);
  const [actions, setActions] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip };
      Object.entries(filters).forEach(([k, v]) => {
        if (v === "" || v === false || v == null) return;
        params[k] = v;
      });
      const { data } = await api.get("/admin/audit-log/", { params });
      setRows(data.audit || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [filters, skip]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/admin/audit-log/actions").then(({ data }) => setActions(data.actions || [])).catch(() => {});
    api.get("/admin/audit-log/entity-types").then(({ data }) => setEntityTypes(data.entity_types || [])).catch(() => {});
  }, []);

  const exportCsv = () => {
    const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/api/admin/audit-log/export.csv`);
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v === false || v == null) return;
      url.searchParams.set(k, String(v));
    });
    const token = localStorage.getItem("token");
    fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `pickngo-audit-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
      });
  };

  const onFilter = (key, val) => {
    setSkip(0);
    setFilters((f) => ({ ...f, [key]: val }));
  };

  return (
    <div className="space-y-6" data-testid="admin-audit-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Audit Log</h1>
          <p className="text-sm text-slate-500">Every admin-write action is permanent and accountable.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="audit-refresh">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button onClick={exportCsv} size="sm" data-testid="audit-export-csv">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <div className="md:col-span-2 lg:col-span-2 relative">
            <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
            <input
              type="text" placeholder="Search description…"
              value={filters.search}
              onChange={(e) => onFilter("search", e.target.value)}
              className="pl-9 pr-3 py-2 w-full text-sm rounded-xl border border-slate-200 focus:ring-2 focus:ring-orange-200 focus:outline-none"
              data-testid="audit-search"
            />
          </div>
          <input
            type="text" placeholder="Actor email"
            value={filters.actor}
            onChange={(e) => onFilter("actor", e.target.value)}
            className="px-3 py-2 text-sm rounded-xl border border-slate-200"
            data-testid="audit-filter-actor"
          />
          <select
            value={filters.action}
            onChange={(e) => onFilter("action", e.target.value)}
            className="px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white"
            data-testid="audit-filter-action"
          >
            <option value="">All actions</option>
            {actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select
            value={filters.entity_type}
            onChange={(e) => onFilter("entity_type", e.target.value)}
            className="px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white"
            data-testid="audit-filter-entity"
          >
            <option value="">All entities</option>
            {entityTypes.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
          <label className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-xl border border-slate-200 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.pii_only}
              onChange={(e) => onFilter("pii_only", e.target.checked)}
              data-testid="audit-filter-pii"
            />
            <span className="text-xs">PII only</span>
          </label>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>Time</Th>
                <Th>Actor</Th>
                <Th>Action</Th>
                <Th>Entity</Th>
                <Th>Description</Th>
                <Th className="text-center w-10">PII</Th>
              </tr>
            </thead>
            <tbody data-testid="audit-table-body">
              {loading && (
                <tr><td colSpan={6} className="text-center py-8 text-slate-400">
                  <Loader2 className="h-5 w-5 mx-auto animate-spin" />
                </td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-slate-400">No audit rows match.</td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr
                  key={r.id}
                  className={`border-b border-slate-50 hover:bg-slate-50/60 ${r.pii ? "bg-amber-50/50" : ""}`}
                  data-testid={`audit-row-${r.id}`}
                >
                  <td className="px-3 py-2.5 text-xs text-slate-500 whitespace-nowrap">{(r.ts || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="px-3 py-2.5">
                    <div className="text-xs text-slate-900">{r.actor_email || "—"}</div>
                    <div className="text-[10px] text-slate-400">{r.actor_role}</div>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-orange-700">{r.action}</td>
                  <td className="px-3 py-2.5">
                    <div className="text-xs">{r.entity_type}</div>
                    <div className="text-[10px] text-slate-400 font-mono">{r.entity_id || "—"}</div>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-slate-700 max-w-xs">{r.description}</td>
                  <td className="px-3 py-2.5 text-center">
                    {r.pii && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 font-semibold">
                        <AlertTriangle className="h-3 w-3" /> PII
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > PAGE_SIZE && (
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <span>Showing {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} of {total}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} data-testid="audit-prev">Prev</Button>
              <Button size="sm" variant="outline" disabled={skip + PAGE_SIZE >= total} onClick={() => setSkip(skip + PAGE_SIZE)} data-testid="audit-next">Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`px-3 py-2.5 text-left text-[10px] uppercase tracking-wider font-semibold text-slate-500 ${className}`}>
      {children}
    </th>
  );
}
